import React from 'react';
import { Form, Row, Col, Select, Input, Tooltip, Icon, Switch, Modal } from 'antd';
import InputMask from '~components/UI/InputMask';
import InputCurrency, { currency2Float } from '~components/UI/InputCurrency';
import get from 'lodash/get';
import { removeCountryCodeFromPhone } from '~/utils/formatters';
import { WhatsappIcon } from '~/icons/WhatsappIcon';
import { WhatsappLink } from './styles';

const Details = ({ form, data, columns }) => {
    const { getFieldDecorator, getFieldValue } = form;
    const [cnpjLoading, setCnpjLoading] = React.useState(false);

    const handleFetchCNPJData = async (cnpj, formInstance) => {
        if (!cnpj) return;
        
        // Clean CNPJ from any formatting
        const cleanCNPJ = cnpj.replace(/[^\d]/g, '');
        
        if (cleanCNPJ.length !== 14) {
            Modal.error({
                title: 'CNPJ Inválido',
                content: 'Por favor, informe um CNPJ válido com 14 dígitos.',
            });
            return;
        }

        try {
            setCnpjLoading(true);
            const response = await fetch(`https://api.opencnpj.org/${cleanCNPJ}`);
            
            if (response.status === 404) {
                Modal.warning({
                    title: 'CNPJ não encontrado',
                    content: 'Não foram encontradas informações para este CNPJ.',
                });
                return;
            }
            
            if (!response.ok) {
                throw new Error('Erro ao buscar dados do CNPJ');
            }
            
            const companyData = await response.json();
            
            // Update form fields with the fetched data
            const fieldsToUpdate = {};
            
            if (companyData.nome_fantasia) {
                fieldsToUpdate.company_name = companyData.nome_fantasia;
            } else if (companyData.razao_social) {
                fieldsToUpdate.company_name = companyData.razao_social;
            }
            
            if (companyData.municipio && companyData.uf) {
                fieldsToUpdate.city = companyData.municipio;
                fieldsToUpdate.state = companyData.uf;
            }
            
            // Set the form fields
            formInstance.setFieldsValue(fieldsToUpdate);
            
            Modal.success({
                title: 'Dados importados com sucesso!',
                content: `Empresa: ${companyData.razao_social || companyData.nome_fantasia || 'N/A'}`,
            });
            
        } catch (error) {
            console.error('Error fetching CNPJ data:', error);
            Modal.error({
                title: 'Erro ao buscar dados',
                content: 'Não foi possível buscar os dados do CNPJ. Tente novamente.',
            });
        } finally {
            setCnpjLoading(false);
        }
    };

    return (
        <Form layout="vertical">
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Nome do Sócio">
                        {getFieldDecorator('name', {
                            initialValue: get(data, 'name'),
                            rules: [{ required: true, message: 'Por favor informe o nome do sócio!' }],
                        })(<Input placeholder="Nome do sócio" />)}
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Etapa do Funil">
                        {getFieldDecorator('column_id', {
                            initialValue: get(data, 'column_id', 1),
                            rules: [{ required: true, message: 'Por favor informe a etapa do funil!' }],
                        })(
                            <Select>
                                {columns.map(column => {
                                    // Only enable Positivo (id: 4) and Negativo (id: 5) columns
                                    const isEnabled = column.id === 4 || column.id === 5;
                                    return (
                                        <Select.Option 
                                            key={column.id} 
                                            value={column.id}
                                            disabled={!isEnabled}
                                        >
                                            {column.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="CNPJ">
                        {getFieldDecorator('cnpj', {
                            initialValue: get(data, 'additional_data.cnpj'),
                        })(
                            <InputMask 
                                mask="99.999.999/9999-99" 
                                placeholder="CNPJ"
                                suffix={getFieldValue('cnpj') ? (
                                    cnpjLoading ? (
                                        <Icon type="loading" style={{ color: '#1890ff' }} />
                                    ) : (
                                        <Tooltip title="Buscar dados da empresa" placement="topRight">
                                            <Icon 
                                                type="search" 
                                                style={{ cursor: 'pointer', color: '#1890ff' }}
                                                onClick={() => handleFetchCNPJData(getFieldValue('cnpj'), form)}
                                            />
                                        </Tooltip>
                                    )
                                ) : <span />
                                }
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Nome da Empresa">
                        {getFieldDecorator('company_name', {
                            initialValue: get(data, 'additional_data.company_name'),
                        })(<Input placeholder="Nome da empresa" />)}
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="WhatsApp">
                                {getFieldDecorator('phone', { initialValue: removeCountryCodeFromPhone(get(data, 'additional_data.phone')) })(
                                    <InputMask
                                        mask="(99) 9999tt999?"
                                        formatChars={{ "9": "[0-9]", "t": "[0-9\-]", "?": "[0-9 ]" }}
                                        maskChar={null}
                                        placeholder="WhatsApp"
                                        suffix={getFieldValue('phone') ?
                                            <Tooltip title="Ir para o Whatsapp" placement="topRight">
                                                <WhatsappLink href={`https://wa.me/55${getFieldValue('phone')}`} target="_blank">
                                                    <WhatsappIcon />
                                                </WhatsappLink>
                                            </Tooltip> : <span />
                                        }
                                    />
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="E-mail">
                                {getFieldDecorator('email', {
                                    initialValue: get(data, 'additional_data.email'),
                                })(<Input placeholder="E-mail" />)}
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Instagram">
                                {getFieldDecorator('instagram', {
                                    initialValue: get(data, 'additional_data.instagram'),
                                })(
                                    <Input
                                        placeholder="Instagram"
                                        addonBefore={
                                            getFieldValue('instagram') ? (
                                                <Tooltip title="Ir para o Instagram" placement="topLeft">
                                                    <a 
                                                        href={`https://instagram.com/${getFieldValue('instagram').replace('@', '')}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1890ff' }}
                                                    >
                                                        <Icon type="instagram" />
                                                    </a>
                                                </Tooltip>
                                            ) : (
                                                <Icon type="instagram" />
                                            )
                                        }
                                    />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item label={<span>Faturamento</span>}>
                        {getFieldDecorator('billing', {
                            initialValue: get(data, 'additional_data.billing'),
                            normalize: (value) => currency2Float(value)
                        })(
                            <InputCurrency />
                        )}
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={<span>Honorário</span>}>
                        {getFieldDecorator('honorary', {
                            initialValue: get(data, 'additional_data.honorary'),
                            normalize: (value) => currency2Float(value)
                        })(
                            <InputCurrency />
                        )}
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label={<span>Funcionários</span>}>
                        {getFieldDecorator('employees', {
                            initialValue: get(data, 'additional_data.employees'),
                        })(
                            <Input />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Cidade">
                        {getFieldDecorator('city', {
                            initialValue: get(data, 'additional_data.city'),
                        })(<Input placeholder="Cidade" />)}
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Estado">
                        {getFieldDecorator('state', {
                            initialValue: get(data, 'additional_data.state'),
                        })(<Input placeholder="Estado" />)}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Observação">
                        {getFieldDecorator('observation', {
                            initialValue: get(data, 'additional_data.observation'),
                        })(<Input.TextArea rows={6} placeholder="Observação" />)}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Revisado">
                        {getFieldDecorator('is_revised', {
                            valuePropName: 'checked',
                            initialValue: get(data, 'additional_data.is_revised', false),
                        })(
                            <Switch />
                        )}
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
};

export default Details;